// Admin → Scrub queue (Context.HeliconflowScrubQueueMeter.md Q-2 / Q-4, D-179), on src/ui. ADMIN ROLE ONLY (the API
// refuses everyone else). The METER: per running / queued build: card, stage, readings done / total, elapsed, spend
// so far, refreshed every 5 s, and each run's full ledger (every model call with its cost, every progress tick, the
// stop). QUEUE CONTROL: reorder, cancel any (a running build stops cleanly at its next call), pause / resume. THE
// BUDGET: the $20 run hard cap, the vision caps, the take-map cap, the screening reserve, edited here and nowhere else.
// This is the run queue and its spend meter, not the scrubber's self-assessment, so it stays (DESIGN rev 2.1 §2.6).
import { useState, useEffect, useCallback } from 'react';
import { ArrowDown, ArrowUp, Pause, Play, ScrollText, Square, X } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Input from '../ui/Input';
import Table from '../ui/Table';
import Skeleton from '../ui/Skeleton';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { clean } from '../shell/copy';

const BUDGET = [
  ['scrub_run_hard_cap_usd', 'Hard limit per build run (USD). Every model call of a run counts; reaching it stops the run cleanly'],
  ['visual_spend_cap_usd', 'Vision: cap per clip (USD)'],
  ['visual_index_build_cap_usd', 'Vision: cap per build (USD)'],
  ['takemap_spend_cap_usd', 'Take map and judge: cap per build (USD)'],
  ['screening_reserve_usd', 'Screening: its own reserve per build (USD)'],
];
const fmtSec = s => { if (s == null) return ''; s = Math.max(0, Math.round(s)); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = s % 60; return h ? `${h}:${String(m).padStart(2, '0')}:${String(x).padStart(2, '0')}` : `${m}:${String(x).padStart(2, '0')}`; };
const usd = v => (v == null ? '' : `$${Number(v).toFixed(2)}`);
const fmtDT = s => (s ? new Date(s).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const progressText = (p, stage) => { const x = p && stage && p[stage]; return x && x.total != null ? `${x.done} / ${x.total}` : (x ? `${x.done}` : ''); };
const none = <span className="ui-text-3">None</span>;
const RUN_TONE = { done: 'ok', failed: 'danger', running: 'info' };
const sentence = s => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ') : s);

export default function ScrubQueueAdmin() {
  const [m, setM] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [budget, setBudget] = useState(null);
  const [budgetErr, setBudgetErr] = useState(false);
  const [draft, setDraft] = useState({});
  const [savingBudget, setSavingBudget] = useState(false);
  const [ledgerOf, setLedgerOf] = useState(null);
  const [ledger, setLedger] = useState([]);
  const confirm = useConfirm();
  const toast = useToast();

  const load = useCallback(async () => {
    try { const r = await api.get('/api/scrub-queue'); setM(r.data); setLoadErr(''); }
    catch (e) { setLoadErr(clean(e.response?.data?.error) || 'The scrub queue is unavailable.'); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);
  useEffect(() => { api.get('/api/scrub-queue/budget').then(r => { setBudget(r.data); setDraft(r.data); }).catch(() => setBudgetErr(true)); }, []);
  useEffect(() => {
    if (!ledgerOf) return undefined;
    let stop = false;
    const f = async () => { try { const r = await api.get(`/api/scrub-queue/runs/${ledgerOf}/ledger`); if (!stop) setLedger(r.data.rows || []); } catch { /* next tick */ } };
    f();
    const t = setInterval(f, 5000);
    return () => { stop = true; clearInterval(t); };
  }, [ledgerOf]);

  const act = async (fn) => { try { await fn(); await load(); } catch (e) { toast(clean(e.response?.data?.error) || 'The action did not go through. Try again.', { tone: 'danger' }); } };
  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      const body = {};
      for (const [k] of BUDGET) if (Number(draft[k]) !== Number(budget[k])) body[k] = Number(draft[k]);
      const r = await api.put('/api/scrub-queue/budget', body);
      setBudget(r.data); setDraft(r.data); toast('Budget saved.');
    } catch (e) { toast(clean(e.response?.data?.error) || 'The budget did not save. Try again.', { tone: 'danger' }); }
    finally { setSavingBudget(false); }
  };
  const stopRun = async (r) => {
    const ok = await confirm({ title: `Stop the build for ${clean(r.cardTitle)}?`, body: 'The run stops cleanly at its next model call. Spend so far is kept in the ledger.', confirmLabel: 'Stop build', destructive: true });
    if (ok) act(() => api.post(`/api/scrub-queue/runs/${r.id}/cancel`));
  };
  const toggleLedger = id => { setLedger([]); setLedgerOf(ledgerOf === id ? null : id); };
  const ledgerButton = r => (
    <Button variant="quiet" icon={ScrollText} data-testid="sq-ledger" aria-pressed={ledgerOf === r.id} onClick={() => toggleLedger(r.id)}>Ledger ({r.ledgerRows})</Button>
  );

  const running = m ? m.running.map(r => ({ ...r, kindOf: 'running' })) : [];
  const queued = m ? m.queued.map((r, i) => ({ ...r, kindOf: 'queued', idx: i })) : [];
  const live = [...running, ...queued];

  const liveColumns = [
    { key: 'pos', label: <span title="Queue position">#</span>, width: 80, render: r => (r.status === 'queued' ? <span className="ui-num">{r.queuePosition}</span> : <Badge tone="info">{sentence(r.status)}</Badge>) },
    { key: 'card', label: 'Card', width: '24%', render: r => (
      <span title={r.cardId} className="ui-stack ui-stack--tight ui-cell-lines">
        <span className="ui-clip">{clean(r.cardTitle)}</span>
        {r.correction ? <span className="ui-clip ui-text-3">“{clean(r.correction.slice(0, 80))}”</span> : null}
      </span>
    ) },
    { key: 'stage', label: 'Stage', render: r => (r.stage ? sentence(r.stage) : none) },
    { key: 'readings', label: 'Readings', render: r => <span className="ui-num" data-testid="sq-readings">{progressText(r.progress, r.stage === 'witness' || r.stage === 'judge' ? r.stage : (r.progress && r.progress.witness ? 'witness' : r.stage))}</span> },
    { key: 'elapsed', label: 'Elapsed', render: r => <span className="ui-num" data-testid="sq-elapsed">{fmtSec(r.elapsedSec)}</span> },
    { key: 'spend', label: 'Spend', width: 176, render: r => (
      <span className="ui-cluster ui-num" data-testid="sq-spend">
        {usd(r.spendUsd)}{r.hardCapUsd ? <span className="ui-text-3">of {usd(r.hardCapUsd)}</span> : null}
        {r.capHit ? <Badge tone="danger">Limit hit</Badge> : null}
      </span>
    ) },
    { key: 'by', label: 'By', render: r => (r.requestedBy ? clean(r.requestedBy) : r.trigger === 'auto' ? 'Auto-build' : none) },
    { key: 'actions', label: <span className="ui-sr">Actions</span>, width: 264, render: r => (
      <span className="ui-cluster">
        {r.kindOf === 'running' && <Button variant="quiet" icon={Square} data-testid="sq-stop" onClick={() => stopRun(r)}>Stop</Button>}
        {r.kindOf === 'queued' && <>
          <Button variant="quiet" iconOnly icon={ArrowUp} label={`Move ${clean(r.cardTitle)} up the queue`} disabled={r.idx === 0} data-testid="sq-up"
            onClick={() => act(() => api.post(`/api/scrub-queue/runs/${r.id}/move`, { direction: 'up' }))} />
          <Button variant="quiet" iconOnly icon={ArrowDown} label={`Move ${clean(r.cardTitle)} down the queue`} disabled={r.idx === queued.length - 1} data-testid="sq-down"
            onClick={() => act(() => api.post(`/api/scrub-queue/runs/${r.id}/move`, { direction: 'down' }))} />
          <Button variant="quiet" icon={X} data-testid="sq-cancel" onClick={() => act(() => api.post(`/api/scrub-queue/runs/${r.id}/cancel`))}>Cancel</Button>
        </>}
        {ledgerButton(r)}
      </span>
    ) },
  ];

  const recentColumns = [
    { key: 'status', label: 'Status', width: 152, render: r => (
      <span className="ui-cluster">
        {RUN_TONE[r.status] ? <Badge tone={RUN_TONE[r.status]}>{sentence(r.status)}</Badge> : <span className="ui-text-2">{sentence(r.status)}</span>}
        {r.capHit ? <Badge tone="danger">Limit hit</Badge> : null}
      </span>
    ) },
    { key: 'card', label: 'Card', width: '22%', render: r => clean(r.cardTitle) },
    { key: 'version', label: 'Version', width: 80, render: r => (r.version ? `v${r.version}` : none) },
    { key: 'finished', label: 'Finished', render: r => <span className="ui-num">{fmtDT(r.finishedAt)}</span> },
    { key: 'elapsed', label: 'Elapsed', width: 88, render: r => <span className="ui-num">{fmtSec(r.elapsedSec)}</span> },
    { key: 'spend', label: 'Spend', width: 88, render: r => <span className="ui-num">{usd(r.spendUsd)}</span> },
    { key: 'error', label: 'Line', render: r => (r.error ? <span title={clean(r.error)}>{clean(r.error)}</span> : '') },
    { key: 'actions', label: <span className="ui-sr">Actions</span>, width: 160, render: r => ledgerButton(r) },
  ];

  const ledgerColumns = [
    { key: 'at', label: 'Time', width: 104, render: l => <span className="ui-num">{new Date(l.at).toLocaleTimeString()}</span> },
    { key: 'kind', label: 'Kind', width: 104 },
    { key: 'stage', label: 'Stage', width: 96, render: l => l.stage || '' },
    { key: 'clip', label: 'Clip and span', render: l => clean([l.clip, l.span].filter(Boolean).join(', ')) },
    { key: 'done', label: 'Done / total', width: 104, render: l => <span className="ui-num">{l.total != null ? `${l.done} / ${l.total}` : (l.done ?? '')}</span> },
    { key: 'cost', label: 'Cost', width: 88, align: 'right', render: l => <span className="ui-num">{l.costUsd ? `$${l.costUsd.toFixed(4)}` : ''}</span> },
    { key: 'cum', label: 'Run total', width: 96, align: 'right', render: l => <span className="ui-num">{l.cumUsd != null ? `$${l.cumUsd.toFixed(4)}` : ''}</span> },
    { key: 'note', label: 'Note', render: l => { const t = clean(`${l.model ? `${l.model} ` : ''}${l.note || ''}`); return <span title={t}>{t}</span>; } },
  ];

  const listState = !m ? (loadErr ? 'error' : 'loading') : 'data';
  const err = { title: 'The scrub queue did not load', message: loadErr, onRetry: load };

  return (
    <div className="ui-stack ui-stack--loose" data-testid="scrub-queue-admin">
      <section className="ui-section" aria-labelledby="sq-title">
        <div className="ui-section__head ui-section__head--center">
          <h2 className="ui-section__title" id="sq-title">Scrub queue</h2>
          {m && <span className="ui-dense ui-text-2" data-testid="sq-paused">{m.paused ? 'Paused. Queued cards wait.' : 'Running. One build at a time, first in, first out.'}</span>}
          {m && (
            <span className="ui-section__actions">
              <Button variant="secondary" icon={m.paused ? Play : Pause} data-testid="sq-pause" onClick={() => act(() => api.post('/api/scrub-queue/pause', { paused: !m.paused }))}>
                {m.paused ? 'Resume queue' : 'Pause queue'}
              </Button>
            </span>
          )}
        </div>
        {m && loadErr && <p className="ui-p ui-field__error" role="alert">{loadErr} The list below is from the last refresh.</p>}
        <div className="ui-region" data-testid="sq-table">
          <Table caption="Running and queued builds" columns={liveColumns} rows={live} state={listState} error={err}
            empty={{ message: 'Nothing running or queued. A build appears here when a card is scrubbed.' }} />
        </div>
      </section>

      {ledgerOf && (
        <section className="ui-section" aria-labelledby="sq-ledger-title" data-testid="sq-ledger-view">
          <div className="ui-section__head ui-section__head--center">
            <h2 className="ui-section__title" id="sq-ledger-title">Ledger for run <span className="ui-num">{ledgerOf.slice(0, 8)}</span></h2>
            <span className="ui-dense ui-text-3">{ledger.length} rows</span>
            <span className="ui-section__actions"><Button variant="quiet" icon={X} onClick={() => setLedgerOf(null)}>Close ledger</Button></span>
          </div>
          <div className="ui-region">
            <Table caption="Run ledger" columns={ledgerColumns} rows={ledger} maxHeight={320} empty={{ message: 'No ledger rows yet. They appear as the run makes calls.' }} />
          </div>
        </section>
      )}

      <section className="ui-section" aria-labelledby="sq-recent-title">
        <h2 className="ui-section__title" id="sq-recent-title">Recent runs (7 days)</h2>
        <div className="ui-region" data-testid="sq-recent">
          <Table caption="Recent runs" columns={recentColumns} rows={m ? m.recent : []} state={listState} error={err}
            empty={{ message: 'No runs finished in the last 7 days.' }} />
        </div>
      </section>

      <section className="ui-section" aria-labelledby="sq-budget-title" data-testid="sq-budget">
        <h2 className="ui-section__title" id="sq-budget-title">Budget</h2>
        {budget ? (
          <div className="ui-region ui-region--pad ui-stack">
            <div className="ui-list">
              {BUDGET.map(([k, label]) => (
                <div key={k} className="ui-pair">
                  <span>{label}</span>
                  <Input aria-label={label} data-testid={`sq-budget-${k}`} type="number" step="0.01" min="0" value={draft[k] ?? ''}
                    onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div><Button variant="primary" data-testid="sq-budget-save" loading={savingBudget} onClick={saveBudget}>Save budget</Button></div>
          </div>
        ) : budgetErr ? (
          <p className="ui-section__note">The budget did not load. Reload the page to edit it.</p>
        ) : (
          <div className="ui-region ui-region--pad ui-stack" role="status" aria-label="Loading the budget"><Skeleton width="60%" /><Skeleton width="60%" /></div>
        )}
      </section>
    </div>
  );
}
